/*
 * Copyright (c) 2022 Certusoft, Inc. All Rights Reserved.
 *
 * This SOURCE CODE FILE, which has been provided by Certusoft as part
 * of a Certusoft product for use ONLY by licensed users of the product,
 * includes CONFIDENTIAL and PROPRIETARY information of Certusoft.
 *
 * USE OF THIS SOFTWARE IS GOVERNED BY THE TERMS AND CONDITIONS
 * OF THE LICENSE STATEMENT AND LIMITED WARRANTY FURNISHED WITH
 * THE PRODUCT.
 */

import { Component, ElementRef, Input, OnInit } from '@angular/core'
import {
    FeatureLanguageAutocompleteModel,
    FeatureLanguageSpec,
    ParsedHintNode,
    SyntacticHintNode,
    SyntaxHighlightingNode,
} from '../../types/app'
import { HttpClient, HttpParams } from '@angular/common/http'
import { CancellationToken, editor, KeyCode, languages, Position, Range } from 'monaco-editor'
import { EntityEvalDetails, EntityType, threedMode, TokenCategory } from '../../types/enums'
import * as _ from 'lodash'

@Component({
    selector: 'app-feature-hint-input',
    templateUrl: './feature-hint-input.component.html',
    styleUrls: ['./feature-hint-input.component.less'],
})
export class FeatureHintInputComponent implements OnInit {
    constructor(private $http: HttpClient, private elRef: ElementRef) {}

    @Input() datum: any
    @Input() assemblyKey!: string
    @Input() partInfo: any
    @Input() mode: threedMode = threedMode.DATUM

    public datumPathMap: any = {}

    // Data structure containing the full specification for the feature hint language.
    // Will only be loaded once, then cached.
    public featureLanguageSpec!: FeatureLanguageSpec
    static featureLanguageRegistered: boolean = false
    private featureEditor!: editor.IStandaloneCodeEditor
    private parseDecorations: string[] = []
    private parseDecorationsHighlight: string[] = []
    // see app.d.ts for more info about FeatureLanguageAutocompleteModel
    private autocompleteModel: FeatureLanguageAutocompleteModel = {
        lastParsedSyntaxInfo: {
            // simple example to act as placeholder
            tokens: ['hole', ' ', 'pattern'],
            tokenStartIndices: [0, 4, 5],
            tokenCategories: [TokenCategory.ENTITY_DEF, TokenCategory.IGNORED, TokenCategory.ENTITY_DEF],
        },
        lastSuccessfulParseText: 'hole pattern',
        lastParsedSyntacticTree: {
            entityType: EntityType.HOLE_PATTERN,
            entityCharacterStart: 0,
            entityCharacterEnd: 12,
            treeNodeConstructionDocsIndex: -1, // basically just garbage filler from here on out
            leafNodeConstructionDocsIndex: -1,
            children: [],
            predicateTexts: undefined,
            completionHints: {
                predicateInsertionPt: 0,
                numPredicates: 0,
                predicateCompletionHints: [],
                maximizerCompletionHints: [],
                maximizerInsertionPts: [0],
            },
            maximizerDocLabels: undefined,
            maximizerTexts: undefined,
            predicateDocLabels: undefined,
        },
        gFunc: [[0, 12]], // for filler input "hole pattern" -> ''
        cursorFunc(position: number): Array<number> {
            let min = Number.POSITIVE_INFINITY
            let max = Number.NEGATIVE_INFINITY
            for (let i = 0; i < this.gFunc.length; i++) {
                const value = this.gFunc[i]
                if (value[0] <= position && value[1] >= position) {
                    min = Math.min(min, i)
                    max = Math.max(max, i)
                }
            }
            return [min, max]
        },
        deleteChar(cursorPosBeforeChar: number): void {
            for (let i = 0; i < this.gFunc.length; i++) {
                const value = this.gFunc[i]
                if (cursorPosBeforeChar < value[0]) {
                    value[0]--
                    value[1]--
                } else if (cursorPosBeforeChar >= value[1]) {
                    // do nothing
                } else {
                    value[1]--
                }
            }
        },
        insertChar(cursorPosition: number): void {
            for (let i = 0; i < this.gFunc.length; i++) {
                const value = this.gFunc[i]
                if (cursorPosition < value[0]) {
                    value[0]++
                    value[1]++
                } else if (cursorPosition > value[1]) {
                    // do nothing
                } else {
                    value[1]++
                }
            }
        },
        setToIdentity(initCursorPositionCount: number): void {
            this.gFunc = []
            for (let i = 0; i < initCursorPositionCount; i++) {
                this.gFunc.push([i, i])
            }
        },
    }
    // retrieves all templates in langaugeSpec.entityDefinitions[targetType] that match the given regex.
    // Information regarding the position of [ENTITY] (i.e. [ARC] or [FACE]) substrings will also be returned.
    getMatchingTreeNodeTemplates(languageSpec: FeatureLanguageSpec, targetType: EntityType, regex: RegExp) {
        const res = []
        const typeDef = languageSpec.entityDefinitions[targetType]
        for (const treeOption of typeDef.treeNodeOptions) {
            if (treeOption.template.match(regex)) {
                // get entity types for args
                const regex = /\[[A-Z_]+\]/gm
                let regexMatch
                const args = []
                while ((regexMatch = regex.exec(treeOption.template)) !== null) {
                    const m = regexMatch[0]
                    args.push({
                        index: regexMatch.index,
                        length: regexMatch.length,
                        type: EntityType[m.substring(1, m.length - 1) as keyof typeof EntityType],
                    })
                }
                res.push({
                    template: treeOption.template,
                    args: args,
                })
            }
        }
        return res
    }

    ngOnInit(): void {
        this.datumPathMap[threedMode.DATUM] = 'featureDescription'
        this.datumPathMap[threedMode.SUBCAT] = 'featureDescription'

        this.initMonacoEditor()

        if (!this.featureLanguageSpec) {
            this.initLanguageSpec()
        }
    }

    initMonacoEditor(): void {
        // set up custom language
        this.registerMonacoFeatureLanguage()

        // set up the editor options
        this.featureEditor = editor.create(this.elRef.nativeElement.querySelector('.feature-text-editor'), {
            value: _.get(this.datum, this.datumPathMap[this.mode]), // the initial text
            minimap: { enabled: false }, // this is a bar on the right side that shows a super-zoomed-out view of the code
            theme: 'featureHintLangTheme', // syntax highlighting colors
            language: 'featureHintLang', // syntax highlighting
            'semanticHighlighting.enabled': true, // we use semantic syntax highlighting
            glyphMargin: true, // for the check/x mark in the margin
            lineNumbersMinChars: 2, // reduce spacing in the line number div
            fixedOverflowWidgets: true, // to promote monaco playing nice with surround html
            wordBasedSuggestions: false, // prevent common word suggestions based on what was previously typed
            overflowWidgetsDomNode: document.querySelector('html body') as HTMLElement, // This places the hover popup in the dom directly, allowing us to control z-index issues
        })
        // see https://github.com/microsoft/monaco-editor/issues/2233
        document.querySelector('html body')?.classList.add('monaco-editor')

        // so we can access the component associated with the editor from inside syntax highlighting/hover events defined in registerMonacoFeatureLanguage()
        ;(<any>this.featureEditor.getModel()).parentComponent = this

        // update datum.featureDescription when the editor is changed
        this.featureEditor.onDidChangeModelContent((event) => {
            _.set(this.datum, this.datumPathMap[this.mode], this.featureEditor.getValue())
            this.datum.descriptionDirty(true)
            if (this.autocompleteModel.lastParsedSyntacticTree) {
                // update autocomplete model's cursorMap
                for (let change of event.changes) {
                    // a change consists of a range [change.rangeOffset, change.rangeOffset + change.rangeLength] replaced by change.text
                    // rangeOffset is the cursor index immediately preceding the range
                    for (let i = 0; i < change.rangeLength; i++) {
                        this.autocompleteModel.deleteChar(change.rangeOffset)
                    }
                    for (let i = 0; i < change.text.length; i++) {
                        this.autocompleteModel.insertChar(change.rangeOffset)
                    }
                }
            }
        })

        // the ctl-space autocomplete trigger
        this.featureEditor.onKeyUp((event) => {
            if (event.keyCode === KeyCode.Space && event.ctrlKey) {
                this.featureEditor.trigger('', 'editor.action.triggerSuggest', {})
            }
        })
    }

    // this is called from inside the syntax highlighting procedure.
    // Perhaps this is not the best form, but it is convenient!
    onParseResult(
        parseSuccessful: boolean,
        parseText: string,
        syntacticTree: SyntacticHintNode,
        syntaxNode: SyntaxHighlightingNode
    ): void {
        if (this.featureEditor) {
            if (parseSuccessful) {
                // set check mark glyph
                this.parseDecorations = this.featureEditor.deltaDecorations(this.parseDecorations, [
                    {
                        range: new Range(1, 1, 1, 1),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'glyphicon glyphicon-ok monaco-margin-check-mark',
                            glyphMarginHoverMessage: { value: 'parse successful' },
                        },
                    },
                ])
            } else {
                // set x mark glyph
                this.parseDecorations = this.featureEditor.deltaDecorations(this.parseDecorations, [
                    {
                        range: new Range(1, 1, 1, 1),
                        options: {
                            isWholeLine: true,
                            glyphMarginClassName: 'glyphicon glyphicon-remove monaco-margin-x',
                            glyphMarginHoverMessage: { value: 'parse unsuccessful' },
                        },
                    },
                ])
            }

            // reset autocomplete state
            if (parseSuccessful) {
                this.autocompleteModel.lastSuccessfulParseText = parseText
                this.autocompleteModel.lastParsedSyntacticTree = syntacticTree
                this.autocompleteModel.lastParsedSyntaxInfo = syntaxNode
                this.autocompleteModel.setToIdentity(parseText.length + 1)
            }
        }
    }

    // onParseResult deals purely with the syntactic structure. onEvaluationResult is for things dealing with
    // the logical structure, i.e. leaf failure indication
    onEvaluationResult(evaluationText: string, logicalStructure: ParsedHintNode): void {
        // check if the current text is equal to the string that was used for evaluation (could have been
        // changed in the meantime)
        if (evaluationText !== this.featureEditor.getValue()) {
            return
        }

        // maps line number to first character on the line
        const lineMap: number[] = [0]
        for (let i = 0; i < evaluationText.length; i++) {
            if (evaluationText.charAt(i) === '\n') {
                lineMap.push(i + 1)
            }
        }
        const getRowColumn = (charIndex: number) => {
            let row = 0
            while (row < lineMap.length && lineMap[row] <= charIndex) {
                row++
            }
            row--
            return [row, charIndex - lineMap[row]]
        }
        const getRange = (charStartInclusive: number, charEndInclusive: number) => {
            const startRowCol = getRowColumn(charStartInclusive)
            const endRowCol = getRowColumn(charEndInclusive)
            // monaco rows/columns are 1-indexed
            return new Range(startRowCol[0] + 1, startRowCol[1] + 1, endRowCol[0] + 1, endRowCol[1] + 1)
        }

        // look for logical nodes that have multiple or no matches
        const decorations = []
        const nodeStack = [logicalStructure]
        while (nodeStack.length > 0) {
            const n = nodeStack.pop()
            if (n && !n.isLeaf) {
                // only consider leaf entities
                nodeStack.push(...n.children)
                continue
            }
            if (n && !n.entityEvaluationSuccessful) {
                const failureReason = n.entityEvaluationDetails
                let className = 'monaco-failed-entity-highlight-multiple-matches'
                let message = 'Multiple matches found for entity'
                if (failureReason === EntityEvalDetails.NO_MATCHES) {
                    className = 'monaco-failed-entity-highlight-no-matches'
                    message = 'No matches found for entity'
                }

                const decoration = {
                    range: getRange(n.entityCharacterStart, n.entityCharacterEnd),
                    options: {
                        isWholeLine: false,
                        className: className, // these class definitions can be found in styles.less
                        hoverMessage: { value: message }, // The hover message will appear appended to any existing hover message
                    },
                }
                decorations.push(decoration)
            }
        }

        // don't overwrite existing decorations
        const prevDecorations = this.parseDecorations.slice()
        this.parseDecorations = this.featureEditor.deltaDecorations(this.parseDecorationsHighlight, decorations)
        // add everything in this.parseDecorations NOT in prevDecorations to this.parseDecorationsHighlight
        while (this.parseDecorationsHighlight.length > 0) {
            this.parseDecorationsHighlight.pop()
        }
        for (let s of this.parseDecorations) {
            if (prevDecorations.indexOf(s) === -1) {
                this.parseDecorationsHighlight.push(s)
            }
        }
    }

    // featureEditor will not be initialized until after this call
    registerMonacoFeatureLanguage(): void {
        // only register the language once
        if (FeatureHintInputComponent.featureLanguageRegistered) {
            return
        }
        FeatureHintInputComponent.featureLanguageRegistered = true

        const fhc = this
        languages.register({ id: 'featureHintLang' })

        const typeMap: any = {}
        let j = 0
        for (let type in TokenCategory) {
            typeMap[type] = j++
        }

        // hover behavior
        languages.registerHoverProvider('featureHintLang', {
            provideHover(
                model: editor.ITextModel,
                position: Position,
                token: CancellationToken
            ): languages.ProviderResult<languages.Hover> {
                // parse model
                const editorText = model.getValue()
                const serverResult = FeatureHintInputComponent.parseFeatureHint(
                    fhc.$http,
                    editorText,
                    fhc.assemblyKey,
                    [-1, 0, 0],
                    [0, 0, 1],
                    '',
                    true
                )
                // maps line number to first character on the line
                const lineMap: number[] = [0]
                for (let i = 0; i < editorText.length; i++) {
                    if (editorText.charAt(i) === '\n') {
                        lineMap.push(i + 1)
                    }
                }

                return {
                    then<TResult1, TResult2>(
                        onfulfilled:
                            | ((value: languages.Hover | undefined | null) => PromiseLike<TResult1> | TResult1)
                            | undefined
                            | null,
                        onrejected: ((reason: any) => PromiseLike<TResult2> | TResult2) | undefined | null
                    ): PromiseLike<TResult1 | TResult2> {
                        return serverResult.then(
                            (value) => {
                                const parseRes: SyntacticHintNode = (<any>value).data.syntacticStructure
                                if (!onfulfilled) {
                                    // this will never happen
                                    return <TResult1>{}
                                }
                                // gotta convert the position to a useful form (i.e. char index)
                                const charIndex = lineMap[position.lineNumber - 1] + position.column - 1 // line numbers are 1-indexed, and apparently so are columns
                                // check in parseRes for the leaf-most node containing charIndex
                                let leafMost = parseRes

                                if (!leafMost) {
                                    // this can happen if the parse totally fails
                                    if (!onfulfilled) {
                                        return <TResult2>{}
                                    }
                                    // onrejected is for real errors
                                    return onfulfilled(null)
                                }

                                let moved = true
                                while (moved) {
                                    moved = false
                                    for (let j = 0; j < leafMost.children.length; j++) {
                                        const child = leafMost.children[j]
                                        if (
                                            child.entityCharacterStart <= charIndex &&
                                            child.entityCharacterEnd >= charIndex
                                        ) {
                                            leafMost = child
                                            moved = true
                                            break
                                        }
                                    }
                                }

                                function linePos(char: number) {
                                    let line = 0
                                    while (lineMap[line] <= char && line < lineMap.length) {
                                        line++
                                    }
                                    line--
                                    // lineMap[line] is the first character of the line'th line
                                    return [line, char - lineMap[line]]
                                }
                                const start = linePos(leafMost.entityCharacterStart)
                                const end = linePos(leafMost.entityCharacterEnd)

                                return onfulfilled({
                                    contents: fhc.getHoverMarkup(leafMost),
                                    range: new Range(
                                        start[0] + 1, // line numbers are 1-indexed
                                        start[1] + 1, // and so are column numbers (whyyyy...?)
                                        end[0] + 1,
                                        end[1] + 1
                                    ),
                                })
                            },
                            (reason) => {
                                if (!onrejected) {
                                    return <TResult2>{}
                                }
                                return onrejected(null)
                            }
                        )
                    },
                }
            },
        })

        // autocomplete provider
        languages.registerCompletionItemProvider('featureHintLang', {
            provideCompletionItems(
                model: editor.ITextModel,
                position: Position,
                context: languages.CompletionContext,
                token: CancellationToken
            ): languages.ProviderResult<languages.CompletionList> {
                // special case: no suggestions for comments
                const line = model.getLineContent(position.lineNumber)
                const commentIndex = line.indexOf('//')
                if (commentIndex >= 0 && commentIndex < position.column - 1) {
                    return { suggestions: [] }
                }

                // "slight" hack here
                const autocompleteModel = (<FeatureHintInputComponent>(<any>model).parentComponent).autocompleteModel
                const cursorIndex = model.getOffsetAt(position)
                const cursorPreImage = autocompleteModel.cursorFunc(cursorIndex)
                // the trimmed version ignores whitespace (is strictly a subset of cursorPreImage)
                const cursorPreImageTrimmed = [cursorPreImage[0], cursorPreImage[1]]
                while (
                    autocompleteModel.lastSuccessfulParseText.charAt(cursorPreImageTrimmed[0]).match(/\s/g) &&
                    cursorPreImageTrimmed[0] < cursorPreImageTrimmed[1]
                ) {
                    cursorPreImageTrimmed[0]++
                }
                while (
                    autocompleteModel.lastSuccessfulParseText.charAt(cursorPreImageTrimmed[1] - 1).match(/\s/g) &&
                    cursorPreImageTrimmed[0] < cursorPreImageTrimmed[1]
                ) {
                    cursorPreImageTrimmed[1]--
                }

                const word = model.getWordUntilPosition(position)
                const range = {
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                    startColumn: word.startColumn,
                    endColumn: word.endColumn,
                }

                // basically, we're going to come up with a bunch of rules for what we want autocomplete to look like in certain
                // circumstances, and then call it a day

                function getLeafMostEntityAndParent(tree: SyntacticHintNode, charIndex: number) {
                    let parent = null
                    let working = tree
                    while (working.children.length > 0) {
                        let foundChild = false
                        for (let child of working.children) {
                            if (child.entityCharacterStart <= charIndex && charIndex <= child.entityCharacterEnd) {
                                parent = working
                                working = child
                                foundChild = true
                                break
                            }
                        }
                        if (!foundChild) {
                            return [parent, working]
                        }
                    }
                    return [parent, working]
                }

                function prettyPrintEntityName(type: EntityType) {
                    let s = '' + type
                    s = s
                        .replace('_', ' ')
                        .split(' ')
                        .map((x) => x.toLocaleUpperCase())
                        .join(' ')
                    return s
                }

                let suggestions = []
                let noCasesApplicable = true

                // replacing a string like "transform from normal [POINT] [DIRECTION]" with a string matching the pattern that compiles, so
                // perhaps "transform from normal vertex front" in this example.
                const replaceEntityStringWithExample = (template: string) =>
                    template
                        .split(/((?=\[[A-Z_]+\])|(?<=\[[A-Z_]+\]))/g)
                        .map((s) =>
                            s.match(/\[[A-Z_]+\]/g)
                                ? fhc.featureLanguageSpec.entityDefinitions[
                                      EntityType[s.substring(1, s.length - 1) as keyof typeof EntityType]
                                  ].simplestExample
                                : s
                        )
                        .join('')
                        .replace(/\[number\]/g, '3.14') // "number" is not an entity, but still need to replace it with something that compiles

                // Case 1: we've deleted exactly one entity (and possibly surrounding white space)
                // Desired behavior: suggest alternative entity definitions that fit in the empty space
                const [entityParent, entityTarget] = getLeafMostEntityAndParent(
                    autocompleteModel.lastParsedSyntacticTree,
                    cursorPreImageTrimmed[0]
                )
                if (
                    entityTarget &&
                    entityTarget.entityCharacterStart <= cursorPreImageTrimmed[0] &&
                    (entityTarget.completionHints.predicateCompletionHints.length === 0 ||
                        cursorPreImageTrimmed[0] <= entityTarget.completionHints.predicateInsertionPt) &&
                    cursorPreImageTrimmed[1] === entityTarget.entityCharacterEnd
                ) {
                    noCasesApplicable = false
                    // Suggest replacement entities. Most of the time, we will just look for alternative definitions of the same
                    // entity. However, there are a few situations where we may allow different entities. For example, "center of arc" may be replaced by "center of obround".
                    const entitySuggestionTypes = [entityTarget.entityType]
                    if (entityParent && entityParent.treeNodeConstructionDocsIndex >= 0) {
                        // get language spec entry for this entity
                        const entityTemplate =
                            fhc.featureLanguageSpec.entityDefinitions[entityParent.entityType].treeNodeOptions[
                                entityParent.treeNodeConstructionDocsIndex
                            ].template
                        const entityReplacementIndex = entityParent.children.indexOf(entityTarget)
                        // get other entities matching template format
                        let searchRegexStr = entityTemplate
                            .split(/\s+/gm)
                            .join('\\s+')
                            .replace(/(\(|\)|\[|\])/gm, '\\$1')
                        // we need to replace the (entityReplacementIndex)th instance of \[ENTITY\] with \[[A-Z_]+\]
                        const s = /\\\[[A-Z_]+\\\]/gm
                        let res
                        let count = 0
                        let index = 0
                        let length = 0
                        while ((res = s.exec(searchRegexStr)) !== null) {
                            if (count === entityReplacementIndex) {
                                index = res.index
                                length = res[0].length
                            }
                            count++
                        }
                        searchRegexStr =
                            searchRegexStr.substring(0, index) +
                            '\\[[A-Z_]+\\]' +
                            searchRegexStr.substring(index + length, searchRegexStr.length)
                        const matches = fhc.getMatchingTreeNodeTemplates(
                            fhc.featureLanguageSpec,
                            entityParent.entityType,
                            new RegExp(searchRegexStr)
                        )
                        for (let match of matches) {
                            const targetType = match.args[entityReplacementIndex].type
                            if (entitySuggestionTypes.indexOf(targetType) === -1) {
                                entitySuggestionTypes.push(targetType)
                            }
                        }
                    }

                    // now go look for suggestions in languageSpec
                    for (let suggestionType of entitySuggestionTypes) {
                        const templates = []
                        const docs = []
                        for (let leafNode of fhc.featureLanguageSpec.entityDefinitions[suggestionType]
                            .leafNodeOptions) {
                            templates.push(leafNode.template)
                            docs.push(leafNode.shortDescription)
                        }
                        for (let treeNode of fhc.featureLanguageSpec.entityDefinitions[suggestionType]
                            .treeNodeOptions) {
                            templates.push(treeNode.template)
                            docs.push(treeNode.shortDescription)
                        }

                        for (let i = 0; i < templates.length; i++) {
                            let template = templates[i]
                            const finalTemplate = replaceEntityStringWithExample(template)
                            suggestions.push({
                                label: { label: finalTemplate, description: prettyPrintEntityName(suggestionType) },
                                kind: languages.CompletionItemKind.Function,
                                documentation: docs[i],
                                insertText: finalTemplate,
                                range: range, // will be redefined in a bit
                            })
                        }
                    }
                }

                // case 2: The text is empty or close enough to it
                if (
                    model.getValue().length === cursorIndex &&
                    model
                        .getValueInRange({
                            startColumn: 1,
                            startLineNumber: 1,
                            endColumn: word.startColumn,
                            endLineNumber: position.lineNumber,
                        })
                        .trim().length === 0
                ) {
                    noCasesApplicable = false
                    // add all entities as options
                    const templates = []
                    const docs = []
                    const types = []
                    for (let entityType in EntityType) {
                        const t: EntityType = EntityType[entityType as keyof typeof EntityType]
                        const def = fhc.featureLanguageSpec.entityDefinitions[t]
                        for (let leaf of def.leafNodeOptions) {
                            templates.push(leaf.template)
                            docs.push(leaf.shortDescription)
                            types.push(t)
                        }
                        for (let tree of def.treeNodeOptions) {
                            templates.push(tree.template)
                            docs.push(tree.shortDescription)
                            types.push(t)
                        }
                    }
                    suggestions = []
                    for (let i = 0; i < templates.length; i++) {
                        let template = templates[i]
                        const finalTemplate = replaceEntityStringWithExample(template)
                        suggestions.push({
                            label: { label: finalTemplate, description: prettyPrintEntityName(types[i]) },
                            kind: languages.CompletionItemKind.Function,
                            documentation: docs[i],
                            insertText: finalTemplate,
                            range: range, // will be redefined in a bit
                        })
                    }
                }

                // case 3: the pre-image is contained within the predicates of an entity
                // In the case of "arc ", that is after the end of the entity, which means we'll need special handling to find any entities separated
                // by only ignored tokens from the cursor pre-image.
                let leftIgnoredCursorPos = cursorIndex
                let leftToken = -1
                let index = 0
                while (
                    index < autocompleteModel.lastParsedSyntaxInfo.tokenStartIndices.length &&
                    autocompleteModel.lastParsedSyntaxInfo.tokenStartIndices[index] < leftIgnoredCursorPos
                ) {
                    index++
                }
                leftToken = index - 1
                while (
                    leftToken >= 0 &&
                    autocompleteModel.lastParsedSyntaxInfo.tokenCategories[leftToken] === TokenCategory.IGNORED
                ) {
                    leftToken--
                }
                const goalToken = leftToken + 1
                if (goalToken < autocompleteModel.lastParsedSyntaxInfo.tokens.length) {
                    leftIgnoredCursorPos = Math.min(
                        cursorIndex,
                        autocompleteModel.lastParsedSyntaxInfo.tokenStartIndices[goalToken]
                    )
                }

                const lCursorPITrimmed = autocompleteModel.cursorFunc(leftIgnoredCursorPos)
                while (
                    autocompleteModel.lastSuccessfulParseText.charAt(lCursorPITrimmed[0]).match(/\s/g) &&
                    lCursorPITrimmed[0] < lCursorPITrimmed[1]
                ) {
                    lCursorPITrimmed[0]++
                }
                while (
                    autocompleteModel.lastSuccessfulParseText.charAt(lCursorPITrimmed[1] - 1).match(/\s/g) &&
                    lCursorPITrimmed[0] < lCursorPITrimmed[1]
                ) {
                    lCursorPITrimmed[1]--
                }
                let [lEntityParent, lEntityTarget] = getLeafMostEntityAndParent(
                    autocompleteModel.lastParsedSyntacticTree,
                    lCursorPITrimmed[0]
                )
                if (
                    lEntityTarget &&
                    (lEntityTarget.completionHints.maximizerCompletionHints.length > 0 ||
                        lEntityTarget.completionHints.predicateCompletionHints.length > 0)
                ) {
                    // there are predicates possible, and we are at a leaf node
                    // Check if the cursor pre image is contained within the predicate range.
                    let containsConnective = false
                    let connectiveIndex = -1
                    let connectiveLength = -1
                    // length - 1 because there is a look ahead below to skip to the next connective token
                    for (let i = 0; i < autocompleteModel.lastParsedSyntaxInfo.tokens.length - 1; i++) {
                        const index = autocompleteModel.lastParsedSyntaxInfo.tokenStartIndices[i]
                        const token = autocompleteModel.lastParsedSyntaxInfo.tokens[i]
                        let j = i + 1
                        // skip to next connective token
                        while (
                            autocompleteModel.lastParsedSyntaxInfo.tokenCategories[j] != TokenCategory.CONNECTIVE &&
                            j < autocompleteModel.lastParsedSyntaxInfo.tokens.length
                        ) {
                            j++
                        }
                        if (j >= autocompleteModel.lastParsedSyntaxInfo.tokens.length) {
                            break
                        }
                        const nextIndex = autocompleteModel.lastParsedSyntaxInfo.tokenStartIndices[j]
                        const nextToken = autocompleteModel.lastParsedSyntaxInfo.tokens[j]
                        if (
                            index >= lEntityTarget.entityCharacterStart &&
                            nextIndex < lEntityTarget.entityCharacterEnd
                        ) {
                            // a token is either completely contained within an entity or completely outside
                            const ntLower = nextToken.toLocaleLowerCase()
                            if (token.toLocaleLowerCase() === 'that' && (ntLower === 'has' || ntLower == 'is')) {
                                containsConnective = true
                                connectiveIndex = index
                                connectiveLength = ntLower === 'has' ? nextIndex - index + 3 : nextIndex - index + 2
                            }
                        }
                    }

                    let acceptableStart = lEntityTarget.completionHints.predicateInsertionPt
                    let acceptableEnd = containsConnective ? connectiveIndex : lEntityTarget.entityCharacterEnd
                    if (
                        isFinite(lCursorPITrimmed[0]) &&
                        lCursorPITrimmed[0] >= acceptableStart &&
                        lCursorPITrimmed[1] <= acceptableEnd
                    ) {
                        noCasesApplicable = false
                        // use predicate hints as suggestions
                        for (let predicateHint of lEntityTarget.completionHints.predicateCompletionHints) {
                            for (let option of predicateHint.possibleTemplates) {
                                suggestions.push({
                                    label: {
                                        label: option,
                                        description: 'Predicate: ' + prettyPrintEntityName(lEntityTarget.entityType),
                                    },
                                    kind: languages.CompletionItemKind.Function,
                                    documentation: predicateHint.label,
                                    insertText: option,
                                    range: range, // will be redefined in a bit
                                })
                            }
                        }
                        // add "that is"/"that has" suggestion when applicable
                        if (
                            lCursorPITrimmed[1] === acceptableEnd &&
                            !containsConnective &&
                            lEntityTarget.completionHints.maximizerCompletionHints.length > 0
                        ) {
                            noCasesApplicable = false
                            for (let connective of ['that is', 'that has']) {
                                suggestions.push({
                                    label: {
                                        label: connective,
                                        description: 'Connective: ' + prettyPrintEntityName(lEntityTarget.entityType),
                                    },
                                    kind: languages.CompletionItemKind.Function,
                                    documentation: 'predicate/maximizer connective',
                                    insertText: connective,
                                    range: range, // will be redefined in a bit
                                })
                            }
                        }
                    }

                    // case 4: maximizer completions
                    if (containsConnective && lEntityTarget.completionHints.maximizerCompletionHints.length > 0) {
                        acceptableStart = connectiveIndex + connectiveLength
                        acceptableEnd = lEntityTarget.entityCharacterEnd
                        if (
                            isFinite(lCursorPITrimmed[0]) &&
                            lCursorPITrimmed[0] >= acceptableStart &&
                            lCursorPITrimmed[1] <= acceptableEnd
                        ) {
                            noCasesApplicable = false
                            for (let maximizerHint of lEntityTarget.completionHints.maximizerCompletionHints) {
                                for (let option of maximizerHint.possibleTemplates) {
                                    suggestions.push({
                                        label: {
                                            label: option,
                                            description:
                                                'Maximizer: ' + prettyPrintEntityName(lEntityTarget.entityType),
                                        },
                                        kind: languages.CompletionItemKind.Function,
                                        documentation: maximizerHint.label,
                                        insertText: option,
                                        range: range, // will be redefined in a bit
                                    })
                                }
                            }
                        }
                    }
                }

                // case 5: we didn't come up with any suggestions, time to guess random entity definitions
                if (suggestions.length === 0 && noCasesApplicable) {
                    const templates = []
                    const docs = []
                    const types = []
                    for (let entityType in EntityType) {
                        const t: EntityType = EntityType[entityType as keyof typeof EntityType]
                        const def = fhc.featureLanguageSpec.entityDefinitions[t]
                        for (let leaf of def.leafNodeOptions) {
                            templates.push(leaf.template)
                            docs.push(leaf.shortDescription)
                            types.push(t)
                        }
                        for (let tree of def.treeNodeOptions) {
                            templates.push(tree.template)
                            docs.push(tree.shortDescription)
                            types.push(t)
                        }
                    }
                    for (let i = 0; i < templates.length; i++) {
                        let template = templates[i]
                        const finalTemplate = replaceEntityStringWithExample(template)
                        suggestions.push({
                            label: { label: finalTemplate, description: prettyPrintEntityName(types[i]) },
                            kind: languages.CompletionItemKind.Function,
                            documentation: docs[i],
                            insertText: finalTemplate,
                            range: range, // will be redefined in a bit
                        })
                    }
                }

                const textToCursor = model.getValueInRange({
                    startColumn: 1,
                    endColumn: position.column,
                    startLineNumber: position.lineNumber,
                    endLineNumber: position.lineNumber,
                })
                for (let suggestion of suggestions) {
                    // compute ideal range for suggestion (suggestions might be multiple words)
                    const suggestionText = suggestion.insertText
                    // We're looking for the position in the text editor which marks the beginning of the suggestion insertion range.
                    // Example 1:
                    //    current text: bix box box<cursor>
                    //    suggestion: box box box
                    //    best insertion: replace "box box" with "box box box"
                    // Example 2:
                    //    current text: aaabbb<cursor>
                    //    suggestion: bb
                    //    best insertion: replace last "bb" with "bb" (i.e. do nothing)
                    // Example 3:
                    //    current text: the quick brown fox jum<cursor>
                    //    suggestion: quickly jumped
                    //    best insertion: replace "jum" with "quickly jumped"
                    // Thus we are looking for the largest i such that currentText.substring(currentText.length - i, currentText.length) is
                    // a substring of suggestion, and that currentText.substring(currentText.length - i, currentText.length) does not start with whitespace!
                    let workingPos = textToCursor.length - 1
                    if (textToCursor.length == 0 || textToCursor.trim().length === 0) {
                        continue // leave as default, the current line is empty
                    }
                    const insertArr = []
                    for (let i = 0; i < suggestionText.length; i++) {
                        if (suggestionText.charAt(i) === textToCursor.charAt(workingPos)) {
                            insertArr.push([i, i])
                        }
                    }
                    if (insertArr.length === 0) {
                        continue // leave the range as the default, there are no matches in suggestion
                    }
                    let hasActiveInsertPt = true
                    workingPos--
                    while (hasActiveInsertPt && workingPos >= 0) {
                        hasActiveInsertPt = false
                        for (let i = 0; i < insertArr.length; i++) {
                            const insertOption = insertArr[i]
                            // this first condition makes sure to only extend options which are so far fully matching
                            if (
                                insertOption[0] === insertOption[1] - (textToCursor.length - 2 - workingPos) &&
                                suggestionText.charAt(insertOption[0] - 1) === textToCursor.charAt(workingPos)
                            ) {
                                insertOption[0]--
                                hasActiveInsertPt = true
                            }
                        }
                        workingPos--
                    }

                    let maxLength = -1
                    for (let i = 0; i < insertArr.length; i++) {
                        // trim off any whitespace from the beginning of options
                        while (suggestionText.charAt(insertArr[i][0]).match(/\s/g)) {
                            insertArr[i][0]++
                        }
                        maxLength = Math.max(maxLength, insertArr[i][1] - insertArr[i][0] + 1)
                    }

                    const startCol = Math.min(word.startColumn, position.column - maxLength)
                    suggestion.range = {
                        startColumn: startCol,
                        endColumn: position.column,
                        startLineNumber: position.lineNumber,
                        endLineNumber: position.lineNumber,
                    }
                }

                return { suggestions: suggestions }
            },
        })

        // Semantic syntax highlighting
        languages.registerDocumentSemanticTokensProvider('featureHintLang', {
            getLegend(): languages.SemanticTokensLegend {
                const types: string[] = []
                for (const type in TokenCategory) {
                    types.push(type)
                }
                return {
                    tokenTypes: types, // these are the token categories that receive different colors
                    tokenModifiers: [], // no necessary modifiers
                }
            },
            provideDocumentSemanticTokens(
                model: editor.ITextModel,
                lastResultId: string | null,
                token: CancellationToken
            ): languages.ProviderResult<languages.SemanticTokens | languages.SemanticTokensEdits> {
                const editorText = model.getValue()
                // parse the text
                const serverResult = FeatureHintInputComponent.parseFeatureHint(
                    fhc.$http,
                    editorText,
                    fhc.assemblyKey,
                    [-1, 0, 0],
                    [0, 0, 1],
                    '',
                    true
                )
                const lineBreakPos: number[] = []
                for (let i = 0; i < editorText.length; i++) {
                    if (editorText.charAt(i) === '\n') {
                        lineBreakPos.push(i)
                    }
                }

                return {
                    // There's some real promise hocus-pocus going on here.
                    // So the monaco library will call this "then" function like so:
                    //    result = await provideDocumentSemanticTokens(model, lastResultId, token);
                    // You can check out the source here: https://github.com/microsoft/vscode/blob/54dec24a4d1e67dc6cb7fae0e6119dc84e0fab92/src/vs/editor/common/services/getSemanticTokens.ts
                    // Why is it that onfulfilled returns a promise? Great question... probably so we can do something like the following
                    then: function <TResult1, TResult2>(
                        onfulfilled:
                            | ((
                                  value: languages.SemanticTokens | languages.SemanticTokensEdits | undefined | null
                              ) => PromiseLike<TResult1> | TResult1)
                            | undefined
                            | null,
                        onrejected: ((reason: any) => PromiseLike<TResult2> | TResult2) | undefined | null
                    ): PromiseLike<TResult1 | TResult2> {
                        return serverResult.then(
                            (value) => {
                                const parseRes: SyntaxHighlightingNode = (<any>value).data.syntaxHighlighting
                                if (!onfulfilled) {
                                    // this will never happen
                                    return <TResult1>{}
                                }

                                // This is pretty much a bald hack... but it works!
                                ;(<FeatureHintInputComponent>(<any>model).parentComponent).onParseResult(
                                    (<any>value).data.parseSuccessful,
                                    editorText,
                                    (<any>value).data.syntacticStructure,
                                    (<any>value).data.syntaxHighlighting
                                )

                                // this data contains packed 5-tuples of integers (a1, b1, c1, d1, e1, a2, b2, c2, d2, e2, ... )
                                // such that
                                //  - ai is a line number, offset from a(i-1)
                                //  - bi is a column number, offset from b(i-1) (except when on new line, then offset from 0)
                                //  - ci is the token length
                                //  - di is the index in legend.tokenTypes of the token
                                //  - ei is the index in legend.tokenModifiers of the token
                                // There are some examples here: https://authzed.com/blog/syntax-and-semantics/

                                // Luckily, our parse data contained in parseRes is somewhat similar; we just need to figure out the line/column numbers from
                                // the tokenStartIndices
                                const data: number[] = []
                                let prevTokenStartIndex = 0
                                let nextLineBreakIndex = 0
                                for (let i = 0; i < parseRes.tokens.length; i++) {
                                    // ignore tokens that are white space
                                    if (parseRes.tokens[i].trim().length === 0) {
                                        continue
                                    }
                                    const tokenStart = parseRes.tokenStartIndices[i]
                                    // determine how many line breaks are between the previous token and this token
                                    let lineBreakCount = 0
                                    while (lineBreakPos[nextLineBreakIndex] < tokenStart) {
                                        nextLineBreakIndex++
                                        lineBreakCount++
                                    }

                                    // now for column offset
                                    let colOffset = tokenStart - prevTokenStartIndex
                                    if (lineBreakCount > 0) {
                                        colOffset = tokenStart - lineBreakPos[nextLineBreakIndex - 1] - 1 // the line break is the last character of the previous line
                                    }
                                    const tokenLength = parseRes.tokens[i].length
                                    const tokenTypeIndex = typeMap[parseRes.tokenCategories[i]]
                                    const tokenModifierIndex = 0 // no modifiers

                                    data.push(
                                        lineBreakCount,
                                        colOffset,
                                        tokenLength,
                                        tokenTypeIndex,
                                        tokenModifierIndex
                                    )

                                    prevTokenStartIndex = tokenStart
                                }

                                return onfulfilled({
                                    data: new Uint32Array(data),
                                    resultId: '',
                                })
                            },
                            (reason) => {
                                if (!onrejected) {
                                    return <TResult2>{}
                                }
                                return onrejected(null)
                            }
                        )
                    },
                }
            },
            releaseDocumentSemanticTokens(resultId: string | undefined): void {},
        })

        const tokenRules = []
        for (let tokenStr in TokenCategory) {
            const style = this.getCharStyle(TokenCategory[tokenStr as keyof typeof TokenCategory])
            tokenRules.push({
                token: tokenStr,
                foreground: style[0],
                fontStyle: style[1],
            })
        }
        // define theme for tokens
        editor.defineTheme('featureHintLangTheme', {
            base: 'vs',
            colors: {},
            inherit: true,
            rules: tokenRules,
        })
    }

    getHoverMarkup(syntacticNode: SyntacticHintNode) {
        let title = syntacticNode.entityType.toLocaleLowerCase()
        // make first letter uppercase, replace "_" with " "
        title = title
            .split('_')
            .map((x) => (x.length == 0 ? '' : x.charAt(0).toUpperCase() + x.substring(1, x.length)))
            .join(' ')

        const isLeaf = syntacticNode.children.length == 0
        let templateStr = ''
        let longDescriptionStr = ''
        let predicateList: any[] = []
        let maximizerList: any[] = []
        if (this.featureLanguageSpec) {
            // may not have loaded yet
            if (syntacticNode.leafNodeConstructionDocsIndex >= 0) {
                const index = syntacticNode.leafNodeConstructionDocsIndex
                if (index >= 0) {
                    const infoNode =
                        this.featureLanguageSpec.entityDefinitions[syntacticNode.entityType].leafNodeOptions[index]
                    templateStr = infoNode.template
                    longDescriptionStr = infoNode.longDescription
                }
            } else {
                const index = syntacticNode.treeNodeConstructionDocsIndex
                if (index >= 0) {
                    const infoNode =
                        this.featureLanguageSpec.entityDefinitions[syntacticNode.entityType].treeNodeOptions[index]
                    templateStr = infoNode.template
                    longDescriptionStr = infoNode.longDescription
                }
            }
            if (syntacticNode.predicateDocLabels && syntacticNode.predicateTexts) {
                for (let i = 0; i < syntacticNode.predicateDocLabels.length; i++) {
                    const predText = syntacticNode.predicateTexts[i]
                    const predDescr =
                        this.featureLanguageSpec.entityDefinitions[syntacticNode.entityType].leafNodeOptions[
                            syntacticNode.leafNodeConstructionDocsIndex
                        ].predicateOptions[syntacticNode.predicateDocLabels[i]]
                    if (predDescr) {
                        // occasionally there can be valid predicates that are nonetheless not included in the documentation because they are redundant;
                        // "circle, arc length 360deg" is one such example.
                        predicateList.push({
                            text: '*predicate:* **' + predText + '**',
                            description: predDescr.longDescription,
                        })
                    }
                }
            }
            if (syntacticNode.maximizerDocLabels && syntacticNode.maximizerTexts) {
                for (let i = 0; i < syntacticNode.maximizerDocLabels.length; i++) {
                    const maxText = syntacticNode.maximizerTexts[i]
                    const maxDescr =
                        this.featureLanguageSpec.entityDefinitions[syntacticNode.entityType].leafNodeOptions[
                            syntacticNode.leafNodeConstructionDocsIndex
                        ].maximizerOptions[syntacticNode.maximizerDocLabels[i]]
                    maximizerList.push({
                        text: '*maximizer:* **' + maxText + '**',
                        description: maxDescr.longDescription,
                    })
                }
            }
        }

        const resArr = []
        resArr.push({ value: '**' + title + '**' })
        resArr.push({ value: templateStr })
        resArr.push({ value: longDescriptionStr })
        for (const pred of predicateList) {
            resArr.push({ value: pred.text + '\n\n' + pred.description })
        }
        for (const max of maximizerList) {
            resArr.push({ value: max.text + '\n\n' + max.description })
        }
        return resArr
    }

    static calculatePreTransform(front: Array<number>, up: Array<number>) {
        // return front.concat(up);
        // Just return default values here (Front = +X, up = +Z), the pre-transform will be added in the assembly
        return [1, 0, 0, 0, 0, 1]
    }

    static parseFeatureHint(
        http: HttpClient,
        hint: string,
        assemblyKey: string,
        front: Array<number>,
        up: Array<number>,
        featureId: string,
        skipEvaluation: boolean,
        editorToUpdate?: FeatureHintInputComponent
    ) {
        let vm = this
        let preTransform = FeatureHintInputComponent.calculatePreTransform(front, up)
        let params = new HttpParams()
            .set('a', assemblyKey)
            .set('fx', preTransform[0])
            .set('fy', preTransform[1])
            .set('fz', preTransform[2])
            .set('ux', preTransform[3])
            .set('uy', preTransform[4])
            .set('uz', preTransform[5])
            .set('se', skipEvaluation)
            .set('n', featureId || '')
        const postPromise = http.post('/api/renderer/parse-features', hint, { params }).toPromise()
        return new Promise(function (resolve, reject) {
            postPromise.then(
                function (result) {
                    // if editorToUpdate is defined, call its evaluation hook
                    if (!skipEvaluation && editorToUpdate) {
                        editorToUpdate.onEvaluationResult(hint, (<any>result).data.logicalStructure)
                    }
                    resolve(result)
                },
                function (error) {
                    reject(error)
                }
            )
        })
    }

    /*
   Syntax highlighting and code completion
  */

    initLanguageSpec() {
        let vm = this
        vm.$http
            .get('/api/renderer/feature-language-spec')
            .toPromise()
            .then(
                function (response: any) {
                    vm.featureLanguageSpec = response.data
                },
                function (response: any) {
                    console.error('Error getting feature language specification.')
                }
            )
    }

    // returns array [color, fontStyle]
    // color is hex string, 'aaaaaa' for example
    // font style is '', 'bold', 'italic' or 'bold italic'
    getCharStyle(tokenCategory: TokenCategory) {
        let s = '#0053D5'
        if (tokenCategory === TokenCategory.CONSTANT || tokenCategory === TokenCategory.NEGATIVE_SIGN_DASH) {
            return ['0053D5', '']
        }
        s = '#000000'
        if (tokenCategory === TokenCategory.RANGE_CONNECTOR) {
            return ['000000', '']
        }
        s = '#5B97FF'
        if (tokenCategory === TokenCategory.UNIT) {
            return ['5B97FF', '']
        }
        s = '#1FAD00'
        if (tokenCategory === TokenCategory.PREDICATE_DESCRIPTOR) {
            return ['1FAD00', '']
        }
        s = '#E7830C'
        if (tokenCategory === TokenCategory.ENTITY_DEF) {
            return ['E7830C', 'bold']
        }
        s = '#000000'
        if (tokenCategory === TokenCategory.CONNECTIVE) {
            return ['000000', 'italic']
        }
        s = '#7E3CE8'
        if (
            tokenCategory === TokenCategory.MAXIMIZER_DESCRIPTOR ||
            tokenCategory === TokenCategory.MAXIMIZER_QUANTIFIER
        ) {
            return ['7E3CE8', '']
        }
        s = '#000000'
        if (tokenCategory === TokenCategory.PAREN_BRACKET) {
            return ['000000', '']
        }
        s = '#C8C8C8'
        if (tokenCategory === TokenCategory.IGNORED) {
            return ['C8C8C8', '']
        }
        s = '#FF0000'
        if (tokenCategory === TokenCategory.UNKNOWN) {
            return ['FF0000', '']
        }
        return ['FF0000', '']
    }
}
